// ─────────────────────────────────────────────────────────────
// App — root component.
// Owns routing, color mode state, theme construction.
//
// Routes:
//   /                 → ScriptHelperApp — CANONICAL app URL
//   /dashboard        → DashboardApp (the Jamie product dashboard)
//   /admin            → AdminPage (admin panel, requires is_admin JWT flag)
//   /discoverstudio  → ScriptHelperApp (alias — see below)
//   /scripthelper     → ScriptHelperApp (alias — pre-rebrand bookmarks)
//
// IMPORTANT — route paths are RELATIVE to the router basename.
// main.tsx mounts <BrowserRouter basename={import.meta.env.BASE_URL}>, and
// BASE_URL is "/discoverstudio/" in prod ("/" in local dev). So `path="/"`
// already resolves to https://…/discoverstudio/ in prod — that is the real,
// canonical app URL and the one nginx redirects to.
//
// Corollary: navigate("/discoverstudio") would resolve to
// /discoverstudio/discoverstudio — the brand name twice. ALWAYS navigate to
// "/" for the main app. The two named routes below exist only as catch-alls so
// a typed URL or an old bookmark still renders the app; nothing navigates TO
// them. Do not reintroduce the doubling.
//
// Component and directory names keep the historical "scriptHelper" spelling —
// the rebrand is display-only.
// ─────────────────────────────────────────────────────────────

import { useMemo, useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { authApi } from "./api/client";
import type { AuthUser } from "./api/client";
import { ThemeProvider, CssBaseline, Tooltip, Button, Box } from "@mui/material";
import { useColorMode } from "./hooks/useColorMode";
import { useMaintenance } from "./hooks/useMaintenance";
import { createDashboardTheme } from "./theme";
import { AppShell, type ViewId } from "./components/AppShell";
import { HomeView } from "./views/HomeView";
import { BacklogView } from "./views/BacklogView";
import { KanbanView } from "./views/KanbanView";
import { JamieChatView } from "./views/JamieChatView";
import { HistoryView } from "./views/HistoryView";
import { ScriptHelperApp } from "./views/ScriptHelperApp";
import { AdminPage } from "./views/scriptHelper/AdminPage";
import { MeetTheTeamDialog } from "./components/MeetTheTeamDialog";
import { MaintenanceScreen } from "./views/MaintenanceScreen";
import { MaintenanceBanner } from "./components/MaintenanceBanner";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

// ── Auth guard — wraps routes that require a valid session ────
function RequireAuth({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    authApi.me().then(() => {
      setReady(true);
    }).catch(() => {
      localStorage.removeItem("auth_token");
      navigate("/", { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;
  return <>{children}</>;
}

// ── Admin guard — wraps routes that require is_admin === true ──
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    authApi.me().then((user) => {
      if (!user.is_admin) {
        navigate("/", { replace: true });
        return;
      }
      setReady(true);
    }).catch(() => {
      localStorage.removeItem("auth_token");
      navigate("/", { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;
  return <>{children}</>;
}

// ── Dashboard application (all views, nav, theme) ────────────
function DashboardApp() {
  const [colorMode, toggleColorMode] = useColorMode();
  const [activeView, setActiveView] = useState<ViewId>("home");
  const [meetTheTeamOpen, setMeetTheTeamOpen] = useState(false);
  // Cross-view navigation hint: when set, KanbanView pre-selects this sprint
  // on its next mount/render. Cleared after consumption so user-driven
  // selection inside KanbanView isn't overridden.
  const [kanbanSprintHint, setKanbanSprintHint] = useState<number | undefined>(undefined);
  const navigate = useNavigate();

  // Site-wide maintenance gate. RequireAuth has already validated the JWT
  // by the time we mount, but we still need the user object to know if
  // they're an admin (admins see the banner; regular users see the
  // maintenance screen). Pulled from localStorage to avoid a second
  // /auth/me round-trip.
  const maintenance = useMaintenance();
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem("auth_user");
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  });
  // Keep our copy of the user fresh so a `set-admin` toggle in another tab
  // doesn't leave us with a stale `is_admin` flag.
  useEffect(() => {
    authApi.me().then(setUser).catch(() => {
      // RequireAuth handles invalid tokens — silently ignore here.
    });
  }, []);

  const theme = useMemo(() => createDashboardTheme(colorMode), [colorMode]);

  // Extra nav item: prominent "back to the main app" action.
  // Outlined MD3 button (not a passive nav row) — signals "leave this section"
  // rather than "switch views within it".
  const appLinkItem = (
    <Tooltip title="Back to Discover Studio" placement="right" arrow>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<ArrowBackOutlinedIcon />}
        onClick={() => navigate("/")}
        sx={{
          justifyContent: "flex-start",
          textTransform: "none",
          fontWeight: 500,
          py: 1,
          px: 1.5,
          borderRadius: "8px",
        }}
      >
        Back to Discover Studio
      </Button>
    </Tooltip>
  );

  // Maintenance gate: regular users see the full maintenance screen;
  // admins see the normal dashboard with a sticky warning banner.
  const isAdmin = user?.is_admin === true;
  const showMaintenanceScreen =
    maintenance.state?.enabled === true &&
    !maintenance.state.adminBypass &&
    !isAdmin;
  const showMaintenanceBanner =
    maintenance.state?.enabled === true && isAdmin;

  if (showMaintenanceScreen) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenanceScreen
          message={maintenance.state!.message}
          updatedAt={maintenance.state!.updatedAt}
          // From the dashboard route, "Admin? Sign in here" doesn't make sense
          // (the user is already inside an authed-only route). We surface no
          // login affordance here — the only path back in is to wait or for
          // an admin to disable maintenance.
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {showMaintenanceBanner && (
        <Box sx={{ position: "sticky", top: 0, zIndex: (t) => t.zIndex.appBar + 2 }}>
          <MaintenanceBanner
            message={maintenance.state!.message}
            updatedAt={maintenance.state!.updatedAt}
            onDisable={() => navigate("/admin#maintenance")}
          />
        </Box>
      )}
      <AppShell
        activeView={activeView}
        onNavigate={setActiveView}
        colorMode={colorMode}
        onToggleColorMode={toggleColorMode}
        onMeetTheTeam={() => setMeetTheTeamOpen(true)}
        extraNavItem={appLinkItem}
      >
        {activeView === "home" && <HomeView onNavigate={setActiveView} />}
        {activeView === "backlog" && <BacklogView />}
        {activeView === "kanban" && (
          <KanbanView
            sprintIdHint={kanbanSprintHint}
            onSprintHintConsumed={() => setKanbanSprintHint(undefined)}
          />
        )}
        {activeView === "jamie" && <JamieChatView />}
        {activeView === "history" && (
          <HistoryView
            onOpenInBoard={(sprintId) => {
              setKanbanSprintHint(sprintId);
              setActiveView("kanban");
            }}
          />
        )}
      </AppShell>
      <MeetTheTeamDialog
        open={meetTheTeamOpen}
        onClose={() => setMeetTheTeamOpen(false)}
      />
    </ThemeProvider>
  );
}

// ── App — router shell ────────────────────────────────────────
export function App() {
  return (
    <Routes>
      <Route path="/" element={<ScriptHelperApp />} />
      <Route path="/dashboard" element={<RequireAuth><DashboardApp /></RequireAuth>} />
      <Route path="/discoverstudio" element={<ScriptHelperApp />} />
      {/* Legacy pre-rebrand alias — keeps old in-app bookmarks working. */}
      <Route path="/scripthelper" element={<ScriptHelperApp />} />
      <Route path="/admin" element={<RequireAdmin><AdminPage /></RequireAdmin>} />
    </Routes>
  );
}
